/*
 * When a conversation changes, everything showing it should agree.
 *
 * There are two lists of conversations on screen at once in the AI console: the chat panel's
 * recents rail and the transcript browser. They fetch independently, so deleting a thread in one
 * left it sitting in the other — still clickable, opening a conversation the server had already
 * removed. Refetching on a timer would paper over it at the cost of polling forever; refetching on
 * focus would still show the stale row until you clicked away and back.
 *
 * The fix is that the delete announces itself. Same shape as `catalogEvents` in model-catalog.ts,
 * which solves the same problem for the model list.
 *
 * TWO CHANNELS, BECAUSE THERE ARE TWO DISTANCES.
 *
 *   The EventTarget carries it within ONE page — which is the case that matters, since the console
 *   renders both lists at once and that is exactly where the disagreement was visible.
 *
 *   The BroadcastChannel carries it to other tabs of the SAME ORIGIN, so a second console tab does
 *   not keep showing a thread you deleted in the first.
 *
 * IT DOES NOT CROSS ORIGINS, and nothing here can. The gateway app and the console are different
 * origins; a delete in one reaches the other when that surface next asks the server, which is the
 * only authority either of them has. Claiming otherwise would mean inventing a sync channel that
 * has no way to be correct.
 */
/** Subscribe with `addEventListener('conversations', e => …)`; the detail is a ConversationChange. */
export const conversationEvents = new EventTarget();
const CHANNEL = 'vitesse.conversations';
/** Same-origin cross-tab. Absent in a test runner and in any browser that lacks it, which is why
 *  every use is optional-chained rather than guarded once and assumed thereafter. */
const channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel(CHANNEL) : null;
// Node implements BroadcastChannel too, and there it is a HANDLE that keeps the event loop alive —
// so importing this module from a test or any server-side render leaves the process running with
// nothing to do. Browsers have no such notion and no `unref`, hence the optional call.
channel?.unref?.();
if (channel) {
    // Arriving from another tab. Re-dispatched locally WITHOUT re-broadcasting — a listener that
    // rebroadcasts what it receives is two tabs talking to each other forever.
    channel.onmessage = (e) => {
        if (e.data?.kind === 'deleted' || e.data?.kind === 'touched')
            emit(e.data);
    };
}
function emit(change) {
    conversationEvents.dispatchEvent(new CustomEvent('conversations', { detail: change }));
}
/**
 * Announce that conversations are gone.
 *
 * Called by whoever performed the delete, AFTER the server confirmed it. Announcing optimistically
 * would remove the row from every list on a request that then failed, and the lists have no way to
 * learn that it failed.
 */
export function conversationsDeleted(ids) {
    if (!ids.length)
        return;
    const change = { kind: 'deleted', ids };
    emit(change);
    channel?.postMessage(change);
}
/**
 * Listen for changes. Returns the unsubscribe, because a listener outliving its view is a leak that
 * only ever shows up as a component updating state nobody is looking at.
 */
/**
 * Announce that a conversation was created or has changed.
 *
 * Called by whoever wrote it, AFTER the write landed. Unlike a delete this does not have to be
 * exact — a spurious `touched` costs a re-read, where a spurious `deleted` removes a row that still
 * exists — so it is safe to fire on any write and let the listeners decide whether they care.
 */
export function conversationsTouched(ids) {
    if (!ids.length)
        return;
    const change = { kind: 'touched', ids };
    emit(change);
    channel?.postMessage(change);
}
export function onConversationChange(fn) {
    const handler = (e) => fn(e.detail);
    conversationEvents.addEventListener('conversations', handler);
    return () => conversationEvents.removeEventListener('conversations', handler);
}
//# sourceMappingURL=conversation-events.js.map